/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetUserSessionIssuerAttachImpactRequest,
  GetUserSessionIssuerAttachImpactSecurity,
} from "../models/operations/getusersessionissuerattachimpact.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildUserSessionIssuerAttachImpactQuery,
  prefetchUserSessionIssuerAttachImpact,
  queryKeyUserSessionIssuerAttachImpact,
  UserSessionIssuerAttachImpactQueryData,
} from "./userSessionIssuerAttachImpact.core.js";
export {
  buildUserSessionIssuerAttachImpactQuery,
  prefetchUserSessionIssuerAttachImpact,
  queryKeyUserSessionIssuerAttachImpact,
  type UserSessionIssuerAttachImpactQueryData,
};

export type UserSessionIssuerAttachImpactQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getAttachImpact userSessionIssuers
 *
 * @remarks
 * Preview what attaching a user_session_issuer to an existing MCP server, toolset, or meta MCP server changes for its clients, before calling the matching update. Name exactly one of mcp_server_id, toolset_id, or meta_mcp_server_id; omit user_session_issuer_id to preview detaching the current issuer. Clients and sessions belong to an issuer, so any change of issuer requires the server's clients to authorize again. Advisory only: the update does not require it. Requires the mcp:write access the matching update requires.
 */
export function useUserSessionIssuerAttachImpact(
  request?: GetUserSessionIssuerAttachImpactRequest | undefined,
  security?: GetUserSessionIssuerAttachImpactSecurity | undefined,
  options?: QueryHookOptions<
    UserSessionIssuerAttachImpactQueryData,
    UserSessionIssuerAttachImpactQueryError
  >,
): UseQueryResult<
  UserSessionIssuerAttachImpactQueryData,
  UserSessionIssuerAttachImpactQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildUserSessionIssuerAttachImpactQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getAttachImpact userSessionIssuers
 *
 * @remarks
 * Preview what attaching a user_session_issuer to an existing MCP server, toolset, or meta MCP server changes for its clients, before calling the matching update. Name exactly one of mcp_server_id, toolset_id, or meta_mcp_server_id; omit user_session_issuer_id to preview detaching the current issuer. Clients and sessions belong to an issuer, so any change of issuer requires the server's clients to authorize again. Advisory only: the update does not require it. Requires the mcp:write access the matching update requires.
 */
export function useUserSessionIssuerAttachImpactSuspense(
  request?: GetUserSessionIssuerAttachImpactRequest | undefined,
  security?: GetUserSessionIssuerAttachImpactSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    UserSessionIssuerAttachImpactQueryData,
    UserSessionIssuerAttachImpactQueryError
  >,
): UseSuspenseQueryResult<
  UserSessionIssuerAttachImpactQueryData,
  UserSessionIssuerAttachImpactQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildUserSessionIssuerAttachImpactQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setUserSessionIssuerAttachImpactData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      mcpServerId?: string | undefined;
      toolsetId?: string | undefined;
      metaMcpServerId?: string | undefined;
      userSessionIssuerId?: string | undefined;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: UserSessionIssuerAttachImpactQueryData,
): UserSessionIssuerAttachImpactQueryData | undefined {
  const key = queryKeyUserSessionIssuerAttachImpact(...queryKeyBase);

  return client.setQueryData<UserSessionIssuerAttachImpactQueryData>(key, data);
}

export function invalidateUserSessionIssuerAttachImpact(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      mcpServerId?: string | undefined;
      toolsetId?: string | undefined;
      metaMcpServerId?: string | undefined;
      userSessionIssuerId?: string | undefined;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "userSessionIssuers",
      "getAttachImpact",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllUserSessionIssuerAttachImpact(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "userSessionIssuers", "getAttachImpact"],
  });
}
